import { Fragment, useEffect, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { getUserDisplayName, getUserLocalLogin, logout, type CurrentUser } from "@/lib/auth";
import { api } from "@/lib/apiClient";
import { ALL_NAV_ITEMS } from "@/lib/nav";
import { useNavAccess } from "@/navigation/useNavAccess";
import { useUserUiPreferences } from "@/lib/UserUiPreferencesProvider";
import { ALL_TOUR_IDS, TOUR_DEFINITIONS } from "@/lib/tours/registry";
import { useTour } from "@/lib/tours/TourContext";

interface AccountMenuProps {
  user: CurrentUser | null;
}

function UserHomePageSetting() {
  const { homePage, setHomePage } = useUserUiPreferences();
  const homePageOptions = useNavAccess(ALL_NAV_ITEMS);

  return (
    <label className="mb-3 block text-sm">
      <span className="mb-1 block font-medium text-label">Startseite</span>
      <select
        className="eh-input w-full text-sm"
        value={homePage}
        onChange={(e) => setHomePage(e.target.value)}
      >
        {homePageOptions.map((item) => (
          <option key={item.key} value={item.href}>
            {item.label}
          </option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-label-tertiary">
        Seite nach dem Anmelden, in Browser und Desktop-App. Standard: Dashboard.
      </span>
    </label>
  );
}

function useEntraGroups(open: boolean) {
  const [groups, setGroups] = useState<string[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || groups !== null || error) return;
    let cancelled = false;
    api
      .get<{ groups?: string[] }>("/me/entra-groups")
      .then((data) => {
        if (cancelled) return;
        setGroups(Array.isArray(data.groups) ? data.groups : []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, groups, error]);

  return { groups, error };
}

function EntraGroupsSection({
  groups,
  error,
}: {
  groups: string[] | null;
  error: boolean;
}) {
  return (
    <div className="mb-3">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
        Entra-Gruppen
      </p>
      {groups === null && !error ? (
        <p className="text-xs text-label-tertiary">Werden geladen…</p>
      ) : null}
      {error ? (
        <p className="text-xs text-label-tertiary">Gruppen konnten nicht geladen werden.</p>
      ) : null}
      {groups && groups.length === 0 ? (
        <p className="text-xs text-label-tertiary">Keine Gruppen</p>
      ) : null}
      {groups && groups.length > 0 ? (
        <ul className="flex max-h-36 flex-wrap gap-1 overflow-y-auto">
          {groups.map((name) => (
            <li
              key={name}
              className="max-w-full break-words rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary"
            >
              {name}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function TourMenuSection() {
  const { startTourById, resetAllToursSeen } = useTour();

  return (
    <div className="mb-3 border-t border-separator pt-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
        Hilfe & Touren
      </p>
      <div className="flex flex-col gap-1">
        {ALL_TOUR_IDS.filter((id) => id !== "shell").map((tourId) => (
          <button
            key={tourId}
            type="button"
            className="rounded-lg px-2 py-1.5 text-left text-sm text-label-secondary transition hover:bg-fill hover:text-label"
            onClick={() => startTourById(tourId, { force: true })}
          >
            Tour: {TOUR_DEFINITIONS[tourId].title}
          </button>
        ))}
        <button
          type="button"
          className="mt-1 rounded-lg px-2 py-1.5 text-left text-xs text-label-tertiary transition hover:bg-fill hover:text-label-secondary"
          onClick={resetAllToursSeen}
        >
          Alle Touren zurücksetzen
        </button>
      </div>
    </div>
  );
}

function AccountMenuPanel({ user, open }: { user: CurrentUser; open: boolean }) {
  const displayName = getUserDisplayName(user) || "Benutzer";
  const localLogin = getUserLocalLogin(user);
  const initials = displayName.trim()[0]?.toUpperCase() || "?";
  const entraGroups = useEntraGroups(open);

  return (
    <>
      <Menu.Button
        className={clsx(
          "inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 md:pl-2.5",
          "text-label transition hover:bg-fill-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        )}
        aria-haspopup="menu"
        aria-label={`Konto von ${displayName}`}
      >
        <span className="hidden max-w-[8rem] truncate text-sm font-medium text-label-secondary md:inline">
          {displayName}
        </span>
        <span
          className="inline-grid h-8 w-8 place-items-center rounded-full bg-accent text-xs font-bold text-label-on-accent"
          aria-hidden
        >
          {initials}
        </span>
        <span className="hidden max-w-[10rem] truncate text-sm font-semibold sm:inline md:hidden">
          {displayName}
        </span>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-1.5rem))] origin-top-right rounded-ios-lg border border-separator bg-card p-3 shadow-ios-md focus:outline-none">
          <div className="mb-3 min-w-0">
            <div className="truncate font-semibold text-label">{displayName}</div>
            {localLogin ? (
              <div className="truncate text-sm text-label-secondary">@{localLogin}</div>
            ) : null}
          </div>
          <EntraGroupsSection groups={entraGroups.groups} error={entraGroups.error} />
          <UserHomePageSetting />
          <TourMenuSection />
          <Button type="button" variant="secondary" size="sm" className="w-full" onClick={() => void logout()}>
            Abmelden
          </Button>
        </Menu.Items>
      </Transition>
    </>
  );
}

export function AccountMenu({ user }: AccountMenuProps) {
  if (!user) return null;

  return (
    <Menu as="div" className="relative">
      {({ open }) => <AccountMenuPanel user={user} open={open} />}
    </Menu>
  );
}
